import { useMemo, useState } from 'react';
import { AlertCircle, Calendar as CalendarIcon, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { usePushHistory, type PushHistoryFilters } from '../hooks/useStockSync';
import { parsePushDetails } from '../utils/parsePushDetails';

type StatusFilter = NonNullable<PushHistoryFilters['status']>;

const ALL_STATUSES = 'all' as const;

// Labels mirror the derived status badges rendered in the table below.
const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_changes', label: 'No changes' },
];

export function PushHistory() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<StatusFilter | typeof ALL_STATUSES>(ALL_STATUSES);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const filters: PushHistoryFilters = useMemo(
    () => ({
      status: status === ALL_STATUSES ? undefined : status,
      fromDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      toDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    }),
    [status, dateRange],
  );

  const { data, isLoading } = usePushHistory(page, filters);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  const hasActiveFilters =
    status !== ALL_STATUSES || Boolean(dateRange?.from) || Boolean(dateRange?.to);

  const handleStatusChange = (value: string) => {
    setStatus(value as StatusFilter | typeof ALL_STATUSES);
    setPage(0);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    setPage(0);
    // Close once a full range is chosen; keep open while picking the end date.
    if (range?.from && range?.to) setDatePickerOpen(false);
  };

  const clearFilters = () => {
    setStatus(ALL_STATUSES);
    setDateRange(undefined);
    setPage(0);
  };

  const dateLabel = dateRange?.from
    ? dateRange.to
      ? `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`
      : format(dateRange.from, 'MMM d, yyyy')
    : 'All dates';

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={handleStatusChange}>
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'h-9 justify-start text-left font-normal',
                !dateRange?.from && 'text-muted-foreground',
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
              defaultMonth={dateRange?.from}
              disabled={{ after: new Date() }}
            />
            {(dateRange?.from || dateRange?.to) && (
              <div className="flex justify-end border-t p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDateRangeChange(undefined)}
                >
                  Clear dates
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1.5">
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : items.length === 0 ? (
        hasActiveFilters ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-lg font-medium">No pushes match these filters</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Try a wider date range or a different status.
            </p>
            <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-lg font-medium">No push history yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Pushes will appear here once inventory sync is active.
            </p>
          </div>
        )
      ) : (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Previous</TableHead>
                  <TableHead className="text-right">Pushed</TableHead>
                  <TableHead className="text-right">Variants</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const { activated, errors: errorMsgs } = parsePushDetails(item.error_details);

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          {item.variants_failed === 0 && item.variants_updated > 0 ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 text-xs">Success</Badge>
                          ) : item.variants_updated > 0 && item.variants_failed > 0 ? (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 text-xs">Partial</Badge>
                          ) : item.variants_failed > 0 ? (
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-300 text-xs">Failed</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">No Changes</Badge>
                          )}
                          {activated > 0 && (
                            <p className="text-xs text-blue-600">{activated} activated</p>
                          )}
                          {item.variants_failed > 0 && errorMsgs.length > 0 && (
                            <p className="text-xs text-red-600 truncate max-w-[250px]" title={errorMsgs.join('; ')}>
                              {errorMsgs[0]}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {item.previous_qty ?? '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {item.pushed_qty}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {item.variants_updated > 0 && (
                          <span className="text-emerald-600">{item.variants_updated}</span>
                        )}
                        {item.variants_failed > 0 && (
                          <span className="text-red-600 ml-1">
                            <AlertCircle className="h-3 w-3 inline mr-0.5" />
                            {item.variants_failed}
                          </span>
                        )}
                        {item.variants_updated === 0 && item.variants_failed === 0 && '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.push_type === 'PAP' ? 'default' : 'secondary'} className="text-xs">
                          {item.push_type === 'PAP' ? 'Pre-made' : item.push_type === 'POD' ? 'On-demand' : item.push_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.triggered_by === 'dry_run' ? (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Dry Run</Badge>
                        ) : item.triggered_by === 'single_sku' ? (
                          <Badge variant="outline" className="text-xs">Single SKU</Badge>
                        ) : item.triggered_by === 'manual' ? (
                          <Badge variant="outline" className="text-xs">Manual</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">Scheduled</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(item.completed_at), { addSuffix: true })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
